/**
 * Meta Pixel (fbq) bootstrap for storefronts.
 * Init + PageView on load; conversion helpers for ViewContent / AddToCart / InitiateCheckout.
 */

let activePixelId = null;
let scriptLoading = false;
// The pixel is initialised on idle (after first paint). Events that fire before that are kept and replayed.
let pendingPixelId = null;
let pendingEvents = [];

/** Tell the tracker a pixel WILL be initialised shortly: early events are queued instead of dropped. */
export function markMetaPixelPending(pixelId) {
  const id = normalizeMetaPixelId(pixelId);
  if (id && !activePixelId) pendingPixelId = id;
}

export const META_PIXEL_CURRENCY = "ILS";

function ensureFbqStub() {
  if (typeof window === "undefined") return null;
  if (typeof window.fbq === "function") return window.fbq;

  const fbq = function (...args) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;
  return fbq;
}

function injectFbeventsScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("meta-pixel-fbevents")) return;
  if (scriptLoading) return;
  scriptLoading = true;
  const script = document.createElement("script");
  script.id = "meta-pixel-fbevents";
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.onload = () => {
    scriptLoading = false;
  };
  script.onerror = () => {
    scriptLoading = false;
  };
  const first = document.getElementsByTagName("script")[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

/** Digits-only Pixel ID, or empty string if invalid. */
export function normalizeMetaPixelId(raw) {
  return String(raw ?? "").replace(/\D/g, "");
}

/**
 * Load Meta Pixel base code (once), init the given Pixel ID, and fire PageView once.
 * Disables Meta autoConfig so Automatic Event Detection (e.g. SubscribedButtonClick)
 * and the automatic PageView do not run — only our explicit track() calls fire.
 */
export function initMetaPixel(pixelId) {
  if (typeof window === "undefined") return false;
  const id = normalizeMetaPixelId(pixelId);
  if (!id) return false;

  const fbq = ensureFbqStub();
  if (!fbq) return false;

  injectFbeventsScript();

  // Already initialized for this pixel on this page — do not init/PageView again
  // (covers React StrictMode double-effects and settings re-renders).
  if (activePixelId === id) {
    return true;
  }

  // Must run BEFORE init: turns off automatic configuration / button-click detection.
  fbq("set", "autoConfig", false, id);
  fbq("init", id);
  activePixelId = id;
  fbq("track", "PageView");
  pendingPixelId = null;
  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach(([name, params, eventId]) => trackMetaEvent(name, params, eventId));
  return true;
}

/** Fire a standard Meta browser event if the pixel is initialized. */
export function trackMetaEvent(eventName, params = {}, eventId = null) {
  if (typeof window === "undefined" || !eventName) return;
  if (!activePixelId) {
    if (pendingPixelId && pendingEvents.length < 20) pendingEvents.push([eventName, params, eventId]);
    return;
  }
  if (typeof window.fbq !== "function") return;
  const options = eventId ? { eventID: String(eventId) } : undefined;
  if (options) {
    window.fbq("track", eventName, params || {}, options);
  } else {
    window.fbq("track", eventName, params || {});
  }
}

function roundMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

/** ViewContent params from a catalog product object. */
export function buildMetaViewContentParams(product) {
  const id = String(product?.id || product?.product_id || "").trim();
  const name = String(product?.name_ar || product?.name || "").trim();
  const value = roundMoney(product?.price ?? product?.base_price ?? 0);
  return {
    content_ids: id ? [id] : [],
    content_type: "product",
    ...(name ? { content_name: name } : {}),
    value,
    currency: META_PIXEL_CURRENCY,
  };
}

/**
 * AddToCart params from a cart line (regular / custom / bundle).
 * @param {object} item
 * @param {(item: object) => number} [unitPriceFn]
 */
export function buildMetaAddToCartParams(item, unitPriceFn) {
  const id = String(item?.id || item?.product_id || "").trim();
  const name = String(item?.name_ar || item?.name || "").trim();
  const qty = Math.max(1, Number(item?.quantity) || 1);
  const unit =
    typeof unitPriceFn === "function"
      ? roundMoney(unitPriceFn(item))
      : roundMoney(item?.price ?? item?.baseUnitPrice ?? 0);
  return {
    content_ids: id ? [id] : [],
    content_type: "product",
    ...(name ? { content_name: name } : {}),
    contents: id ? [{ id, quantity: qty, item_price: unit }] : [],
    value: roundMoney(unit * qty),
    currency: META_PIXEL_CURRENCY,
    num_items: qty,
  };
}

/**
 * InitiateCheckout params from the full cart.
 * @param {object[]} cart
 * @param {(item: object) => number} unitPriceFn
 */
export function buildMetaInitiateCheckoutParams(cart, unitPriceFn) {
  const lines = Array.isArray(cart) ? cart : [];
  const contents = [];
  const contentIds = [];
  let value = 0;
  let numItems = 0;
  for (const item of lines) {
    const id = String(item?.id || item?.product_id || "").trim();
    const qty = Math.max(1, Number(item?.quantity) || 1);
    const unit =
      typeof unitPriceFn === "function"
        ? roundMoney(unitPriceFn(item))
        : roundMoney(item?.price ?? item?.baseUnitPrice ?? 0);
    value += unit * qty;
    numItems += qty;
    if (id) {
      contentIds.push(id);
      contents.push({ id, quantity: qty, item_price: unit });
    }
  }
  return {
    content_ids: contentIds,
    content_type: "product",
    contents,
    value: roundMoney(value),
    currency: META_PIXEL_CURRENCY,
    num_items: numItems,
  };
}

export function trackMetaViewContent(product) {
  trackMetaEvent("ViewContent", buildMetaViewContentParams(product));
}

export function trackMetaAddToCart(item, unitPriceFn) {
  trackMetaEvent("AddToCart", buildMetaAddToCartParams(item, unitPriceFn));
}

export function trackMetaInitiateCheckout(cart, unitPriceFn) {
  trackMetaEvent("InitiateCheckout", buildMetaInitiateCheckoutParams(cart, unitPriceFn));
}
